import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetRaceQuery } from '../api/apiSlice';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { formatLongDate } from '../utils/formatDate';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

function RaceDetail() {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const { id } = useParams<{ id: string }>();
    const { data: race, isLoading } = useGetRaceQuery(Number(id), { skip: !id });

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    if (!race) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography color="text.secondary">{t('races.notFound')}</Typography>
            </Container>
        );
    }

    return (
        <Box sx={{ paddingTop: '10px' }}>
            <Grid container spacing={0.5}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ padding: '10px' }}>
                        <Paper elevation={3}>
                            <Box
                                component="img"
                                src={race.PRESENTATION_IMAGE || PLACEHOLDER_IMAGE}
                                onError={handleImageError}
                                alt={race.TITLE}
                                sx={{ width: '100%', display: 'block' }}
                            />
                        </Paper>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ padding: '10px' }}>
                        <Paper elevation={3} sx={{ padding: '16px', textAlign: 'start' }}>
                            <Typography variant="h4" gutterBottom>
                                {race.TITLE}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <Chip label={sportTypeLabel(race.SPORT_TYPE)} />
                                <Typography variant="body2" color="text.secondary">
                                    {formatLongDate(race.DATE, i18n.language)}
                                </Typography>
                            </Box>
                            {race.LOCATION && <Typography variant="body1" gutterBottom>{race.LOCATION}</Typography>}
                            {race.DESCRIPTION && (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 2 }}>
                                    {race.DESCRIPTION}
                                </Typography>
                            )}
                            <Box display="flex" gap={2} mt={3}>
                                {race.RULES_PDF_URL && (
                                    <Link href={race.RULES_PDF_URL} target="_blank" rel="noopener noreferrer">
                                        <Button variant="outlined" color="primary">
                                            {t('races.rulesPdf')}
                                        </Button>
                                    </Link>
                                )}
                                {race.REGISTRATION_URL && (
                                    <Link href={race.REGISTRATION_URL} target="_blank" rel="noopener noreferrer">
                                        <Button variant="contained" color="primary">
                                            {t('races.register')}
                                        </Button>
                                    </Link>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default RaceDetail;
